export const capabilities = [
  {
    id: "rd",
    number: "01",
    title: "R&D & Feedstock Intelligence",
    stages: "Stages 01\u201302",
    tagline: "Know what your biomass can actually do, before you commit CAPEX.",
    items: [
      "Biomass Characterization",
      "Feedstock Mapping",
      "Lab Scale Validation",
      "Pilot Scale Validation",
      "Reactor Suitability Analysis",
      "Technology Suitability",
      "Commercial Scale Validation",
      "Process Optimization",
      "Performance Benchmarking",
    ],
  },
  {
    id: "engineering",
    number: "02",
    title: "Process & Technology Engineering",
    stages: "Stage 02",
    tagline: "The core engineering deliverables that make a plant buildable and bankable.",
    items: [
      "FEED",
      "PFD & UFD",
      "P&ID",
      "HMBD (Heat & Mass Balance)",
      "Equipment Sizing",
      "Basis of Design",
      "Process Simulation",
      "Scale-up Modelling",
      "HAZOP & Safety Review",
      "Automation & PLC/SCADA",
    ],
  },
  {
    id: "development",
    number: "03",
    title: "Project Development & Advisory",
    stages: "Stages 01\u201304",
    tagline: "From feasibility to commissioning, with a single accountable partner.",
    items: [
      "DPR / PDD Preparation",
      "TEA (Techno-Economic Assessment)",
      "TEEA",
      "CAPEX / OPEX Estimation",
      "Vendor Selection",
      "Project Scheduling",
      "EPCM",
      "Project Management",
      "Commissioning",
      "Performance Optimization",
      "Financial Close Support",
      "Audit Support (Pre- and Post-Listing)",
      "Lab Support",
      "Delivery Risk Assessment",
    ],
  },
  {
    id: "carbon",
    number: "04",
    title: "Carbon Credits & Sustainability",
    stages: "Stages 01 & 05",
    tagline: "Turn tonnes of carbon sequestered into a verified, monetizable revenue line.",
    items: [
      "Carbon Project Design",
      "MRV (via Convergent)",
      "LCA",
      "ESG Consulting",
      "Carbon Revenue Modelling",
      "Additionality Assessment",
      "Buffer Pool & Permanence Analysis",
      "Registry Listing (Verra / Puro.earth / Gold Standard)",
      "Carbon Buyer Matchmaking",
    ],
  },
  {
    id: "bioenergy",
    number: "05",
    title: "Conversion Technologies & Future Fuels",
    stages: "Stage 03",
    tagline: "The reactor and downstream technology that defines yield, quality and cost.",
    items: [
      "Pyrolysis Systems",
      "Torrefaction Systems (Bio-coal)",
      "Gasification Systems",
      "CBG / Bio-Methane & Biogas Upgrading",
      "Steam Drying Systems",
      "Bio-Oil Distillation",
      "ATFE Systems",
      "Bio-Refinery",
      "Hydrogen Extraction",
      "Heat Recovery Systems",
    ],
  },
  {
    id: "handling",
    number: "06",
    title: "Material Handling Integration",
    stages: "Stage 03",
    tagline: "The unglamorous engineering that decides whether a plant actually runs.",
    items: [
      "Biomass Handling Systems",
      "Preprocessing Systems",
      "Ash / Char Handling",
      "Automatic Plant Integration",
      "Storage & Silo Design",
      "Conveying Systems",
      "Dust & Emissions Control",
      "Weighbridge & Logistics",
    ],
  },
  {
    id: "application",
    number: "07",
    title: "Application Engineering & End-Use",
    stages: "Stage 05",
    tagline: "Product doesn't leave the yard until it has a validated market.",
    items: [
      "Biochar Application",
      "Biochar Field Trials",
      "Bio-coal",
      "Industrial Fuel Replacement",
      "Bio-Oil Application",
      "Bio-Methane / CBG Application",
      "Post-Application MRV",
      "Wood Vinegar",
      "Product Development",
      "Soil Amendment Blending",
      "Livestock & Odor Control Applications",
      "Water Filtration Applications",
      "Asphalt Use Case Support",
      "Building Materials Support",
    ],
  },
  {
    id: "field",
    number: "08",
    title: "Farmer & Field Program Design",
    stages: "Stages 01 & 05",
    tagline:
      "Turn residue-owning farmers into a repeatable, MRV-grade supply and impact partner.",
    items: [
      "Farmer Training Workshops",
      "MRV Field Data Collection",
      "Return on Residue (RoR) Tracking",
      "Free-Trial De-Risking Program Design",
      "Multi-Season Validation Design",
      "Community Engagement & Safeguarding",
    ],
  },
  {
    id: "policy",
    number: "09",
    title: "Policy, Market Access & Brand Partnerships",
    stages: "Stages 01 & 05",
    tagline:
      "Position the plant, the product and the credits in the markets that pay a premium.",
    items: [
      "Certification Support (EBC / IBI, ISCC)",
      "Offtake Negotiation",
      "Brand Partnership Structuring",
      "Farmer Premium & Value-Share Design",
      "SBTi / Scope 3 Insetting Advisory",
      "Regulatory & Compliance Mapping",
      "Grant & Blended Finance Applications",
      "Government Liaison & Subsidy Access",
      "Market Intelligence & Competitor Benchmarking",
    ],
  },
];

export const processSteps = [
  {
    number: "01",
    title: "Assess & Design",
    description:
      "Start with your feedstock, site or ambition. We tell you what your biomass can do and which route makes money.",
    services: [
      "Biomass characterization & feedstock mapping",
      "Technology selection: pyrolysis, gasification, torrefaction or CBG",
      "Techno-economic assessment & CAPEX / OPEX",
      "DPR & feasibility reports",
      "Revenue & carbon model scoping",
    ],
  },
  {
    number: "02",
    title: "Engineer & Validate",
    description:
      "Engineering you can hand to a fabricator, validated against real operating data.",
    services: [
      "FEED & basis of design",
      "PFD, P&ID & heat and mass balance",
      "Equipment sizing & process simulation",
      "HAZOP & safety review",
      "Pilot-scale validation",
    ],
  },
  {
    number: "03",
    title: "Build & Integrate",
    description:
      "Reactor, handling and controls engineered as one plant, not stitched together from separate vendors.",
    services: [
      "Pyrolysis, torrefaction, gasification & CBG systems",
      "Biomass handling, drying & preprocessing",
      "Char / ash handling & emissions control",
      "PLC / SCADA automation",
      "Vendor selection & EPCM",
    ],
  },
  {
    number: "04",
    title: "Commission & Optimize",
    description:
      "We stay until the plant hits the yield, quality and uptime the model promised.",
    services: [
      "Commissioning & SOPs",
      "Performance benchmarking",
      "Yield & uptime optimization",
      "Heat recovery",
      "Operator training",
    ],
  },
  {
    number: "05",
    title: "Certify & Monetize Outputs",
    description:
      "Every output becomes revenue: product, fuel, energy or verified carbon.",
    services: [
      "MRV & LCA",
      "Carbon credit registry listing",
      "Certification support (EBC)",
      "Offtake & end-use applications for biochar, bio-oil, bio-coal and CBG",
      "Grants, subsidies & policy access",
    ],
  },
];

export const team = [
  {
    initials: "RN",
    name: "Rohit Nagargoje",
    role: "CEO",
    linkedin: "https://www.linkedin.com/in/rohitnagargoje/",
    photo:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/znpjc41u_6784fe515db9e2462be229fe_Rohit%20Nagargoje.png",
    bio: "Rohit has spent his career at the intersection of engineering and commercial strategy in the carbon removal and biofuel space, having co-founded and helped scale a biochar and biofuel venture from early pilots to commercial operations. His background traces to DTU (Technical University of Denmark), including research on membrane technology at Aquaporin. At Tanren, he leads strategy and client partnerships, bringing a full-chain view to every engagement.",
  },
  {
    initials: "BC",
    name: "Bobby Choudhary",
    role: "Director, Operations",
    linkedin: "https://www.linkedin.com/in/bobbychoudhary/",
    photo:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/na7d5ss2_6784ef8da8dff024bf0e1e28_Bobby.png",
    bio: "Bobby built his career on the plant floor — running test centers and commissioning pyrolysis and biochar units where the engineering meets reality. He's spent years in the gap between a design on paper and a plant that actually holds up day after day. At Tanren, he leads project execution and operations, the phase most projects underestimate.",
  },
  {
    initials: "PS",
    name: "Priyanka Sathe",
    role: "Director, Biochar Science & Carbon Quantification",
    linkedin: "https://www.linkedin.com/in/priyanka-sathe-phd-95916b155/",
    photo:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/wtyx6ev6_6784f95134f4e82d81aa6dc1_Priyanka.png",
    bio: "Priyanka holds a PhD in Biotechnology from the Institute of Chemical Technology and a Master's from Tamil Nadu Agricultural University, with earlier research at the National Research Centre for Grapes. Over more than a decade, she has led large-scale field programs across diverse geographies — biochar ageing and stability studies, carbon fraction quantification, and field sampling frameworks built to hold up under MRV scrutiny. She authored the Return on Residue (RoR) framework linking biomass regeneration to soil carbon outcomes. At Tanren, she directs biochar science and carbon quantification, translating field-level complexity into verifiable, auditable carbon results.",
  },
  {
    initials: "SV",
    name: "Srikanth Vishwanath",
    role: "Director - Carbon Markets & Certification",
    linkedin: "https://www.linkedin.com/in/srikanthvishwanath/",
    photo:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_site-creator-3339/artifacts/6v2bmdq7_6784ec242c567ba362823b79_Srikanth.png",
    bio: "Srikanth has spent his career at the intersection of carbon markets and certification — leading due diligence on carbon removal transactions, structuring offtake agreements, and driving certification under standards including Puro.earth, the Global Biochar C-Sink, and ISCC EU. He's also designed MRV systems end-to-end, from feedstock sourcing through to verified output. At Tanren, he leads carbon markets, certification, and MRV, making sure every tonne claimed is a tonne that holds up to scrutiny.",
  },
];

export const comparisonRows = [
  {
    label: "Engineering depth",
    values: [true, "partial", false, "partial"],
  },
  {
    label: "Carbon & MRV expertise",
    values: [true, false, "partial", false],
  },
  {
    label: "Fabrication & material handling know-how",
    values: [true, "partial", false, false],
  },
  {
    label: "Single accountable partner",
    values: [true, true, false, false],
  },
  {
    label: "Speed to commissioning",
    values: [true, false, "partial", false],
  },
  {
    label: "Post-commissioning support",
    values: [true, "partial", false, false],
  },
];
